import { computed, onScopeDispose, ref, toValue, type MaybeRefOrGetter } from 'vue'
import type { AnalyticsTracker } from '@/tracking/analytics/analyticsTracker'
import { GENERATED_ASSESSMENT_EVENTS } from './event_names'
import {
  DETAIL_ENDING_PHASE_TYPE,
  DETAIL_PHASE_TYPE,
  detailForCountPhase,
  detailForPhase,
  isCountPhase,
  stageForPhase,
  stageOrder,
  type GenerationStageKey
} from './generation_phases'
import {
  GenerationFailedError,
  GenerationIncompleteError,
  GenerationRequestError,
  streamToolkitGeneration,
  type GenerationPayload,
  type GenerationPhase,
  type GenerationRun
} from './generation_stream'
import { annualGenerationTarget, generationGrade, type GenerationTarget } from './generation_targets'

export type GenerationOutcomeKind = 'failed' | 'unknown'

export interface GenerationOutcome {
  error: string
  kind: GenerationOutcomeKind
}

/** What the generate modal submits: the teacher's directions, or null for none. */
export interface GenerationRequest {
  teacherDirections?: string | null
}

export interface AssessmentGenerationOptions {
  savedPageUuid: MaybeRefOrGetter<string>
  goalUuid: MaybeRefOrGetter<string>
  grades: MaybeRefOrGetter<string[] | undefined>
  /** The page's tracker, whose context supplies saved_goal_uuid. */
  analytics: AnalyticsTracker
  refreshGeneratedAssessments: () => Promise<unknown>
}

export const GENERATION_MESSAGES = {
  NO_TARGET_TEXT: 'This target has no text yet. Add the goal text, then generate.',
  NO_GRADE: 'This page has no grade set, which is needed to generate an assessment.',
  UNKNOWN_OUTCOME: 'The connection dropped before this finished. It may still have been saved - reload the page to check.',
  NOT_PERMITTED: "You can't generate an assessment on this page.",
  REFUSED: 'There was an error generating the assessment. Please try again, or contact support if it keeps happening.',
  UNREACHABLE: 'There was an error generating the assessment. Check your network connection and try again.'
}

const NOT_PERMITTED_STATUS = 404
const NO_TURN = -1

function targetKey({ targetType, targetLevel }: GenerationTarget): string {
  return `${targetType}:${targetLevel ?? ''}`
}

// A request the server answered is not a network problem, and 404 is permanent, so it invites no retry.
function failureMessage(error: unknown): string {
  if (error instanceof GenerationRequestError && error.status === NOT_PERMITTED_STATUS) return GENERATION_MESSAGES.NOT_PERMITTED
  if (error instanceof GenerationRequestError || error instanceof GenerationFailedError) return GENERATION_MESSAGES.REFUSED
  return GENERATION_MESSAGES.UNREACHABLE
}

/**
 * The saved anchor page's assessment generation: which target the generate modal is about,
 * the one run in flight and its progress, and each target's last failed or unknown outcome.
 * Outcomes are kept per target, so one that drops while the teacher looks elsewhere is still
 * there when they come back rather than offering a fresh form for work that may be saved.
 * The run belongs to the page, not the modal: closing the modal never stops it.
 */
export function useAssessmentGeneration(options: AssessmentGenerationOptions) {
  const { analytics } = options

  const isModalOpen = ref(false)
  const target = ref<GenerationTarget>(annualGenerationTarget(''))
  const isRunning = ref(false)
  const stage = ref<GenerationStageKey>('starting')
  // What is happening right now, unlike the stage, so it may change in any direction or go blank.
  const detail = ref('')
  const detailTurn = ref(NO_TURN)
  // A tool's line belongs to a turn and ends with it; a count belongs to its stage.
  const isDetailTurnScoped = ref(true)
  const tick = ref(0)
  const resultUuid = ref<string | null>(null)
  const outcomes = ref<Record<string, GenerationOutcome>>({})
  let activeRun: GenerationRun | null = null
  let isTornDown = false

  const openTargetOutcome = computed(() => outcomes.value[targetKey(target.value)] ?? null)
  const errorMessage = computed(() => openTargetOutcome.value?.error ?? null)
  const errorKind = computed(() => openTargetOutcome.value?.kind ?? null)

  function setOutcome(key: string, error: string, kind: GenerationOutcomeKind) {
    outcomes.value = { ...outcomes.value, [key]: { error, kind } }
  }

  function clearOutcome(key: string) {
    const remaining = { ...outcomes.value }
    delete remaining[key]
    outcomes.value = remaining
  }

  function setTurnDetail(text: string, turn: unknown) {
    detail.value = text
    detailTurn.value = Number.isInteger(turn) ? turn as number : NO_TURN
    isDetailTurnScoped.value = true
  }

  function setStageDetail(text: string) {
    detail.value = text
    detailTurn.value = NO_TURN
    isDetailTurnScoped.value = false
  }

  // A completion whose turn can't be read still ends the line: some turn ended, and keeping a
  // label up on a guess is the stale-label bug this exists to prevent.
  function endTurnDetail(turn: unknown) {
    if (!detail.value || !isDetailTurnScoped.value) return
    if (!Number.isInteger(turn) || (turn as number) >= detailTurn.value) setTurnDetail('', NO_TURN)
  }

  // Every event ticks; a recognised one moves the stage forward only, and a stage advance
  // clears the detail, so a count for the new stage is written after it.
  function advance(phase: GenerationPhase) {
    tick.value += 1
    const turn = phase.data.turn
    if (phase.type === DETAIL_PHASE_TYPE) {
      setTurnDetail(detailForPhase(phase) ?? '', turn)
      return
    }
    if (phase.type === DETAIL_ENDING_PHASE_TYPE) endTurnDetail(turn)

    const phaseStage = stageForPhase(phase.type)
    if (phaseStage && stageOrder(phaseStage) > stageOrder(stage.value)) {
      stage.value = phaseStage
      setTurnDetail('', NO_TURN)
    }
    // A late count for a stage the run has already left says nothing about now.
    if (isCountPhase(phase.type) && phaseStage === stage.value) {
      setStageDetail(detailForCountPhase(phase) ?? '')
    }
  }

  function refresh() {
    return options.refreshGeneratedAssessments().catch((error) => {
      console.error('Failed to refresh generated assessments:', error)
    })
  }

  function openModal(nextTarget: GenerationTarget) {
    target.value = { ...nextTarget }
    resultUuid.value = null
    isModalOpen.value = true
  }

  function requestPayload(runTarget: GenerationTarget, grade: string, teacherDirections?: string | null): GenerationPayload {
    const payload: GenerationPayload = {
      saved_page_uuid: toValue(options.savedPageUuid),
      goal_uuid: toValue(options.goalUuid),
      target_type: runTarget.targetType,
      target_text: runTarget.targetText,
      goal_text: runTarget.targetText,
      grade,
      teacher_directions: teacherDirections
    }
    // The route rejects a target_level sent with an annual target.
    if (runTarget.targetType === 'objective' && runTarget.targetLevel) payload.target_level = runTarget.targetLevel
    return payload
  }

  async function recordFailure(runKey: string, error: unknown) {
    console.error('Assessment generation failed:', error)
    // An abort this page caused on leaving is not an outcome: the server still saves the run.
    if (isTornDown) return
    if (error instanceof GenerationIncompleteError) {
      analytics.sendEvent(GENERATED_ASSESSMENT_EVENTS.GENERATION_COMPLETED, { outcome: 'unknown' })
      setOutcome(runKey, GENERATION_MESSAGES.UNKNOWN_OUTCOME, 'unknown')
      await refresh()
      return
    }
    analytics.sendEvent(GENERATED_ASSESSMENT_EVENTS.GENERATION_COMPLETED, { outcome: 'failed' })
    setOutcome(runKey, failureMessage(error), 'failed')
  }

  async function startGeneration({ teacherDirections }: GenerationRequest = {}) {
    // One run at a time: the progress state belongs to the run in flight.
    if (activeRun) return
    const runTarget = target.value
    const runKey = targetKey(runTarget)
    const grade = generationGrade(toValue(options.grades))
    // Checked here because the route answers an empty field with a 400 the teacher would see
    // as the modal returning to a blank form.
    if (!runTarget.targetText) {
      setOutcome(runKey, GENERATION_MESSAGES.NO_TARGET_TEXT, 'failed')
      return
    }
    if (!grade) {
      setOutcome(runKey, GENERATION_MESSAGES.NO_GRADE, 'failed')
      return
    }

    isRunning.value = true
    stage.value = 'starting'
    setTurnDetail('', NO_TURN)
    tick.value = 0
    resultUuid.value = null
    clearOutcome(runKey)

    const run = streamToolkitGeneration(requestPayload(runTarget, grade, teacherDirections), advance)
    activeRun = run
    // Sent once the request is issued, so it counts runs that started rather than clicks.
    analytics.sendEvent(GENERATED_ASSESSMENT_EVENTS.CLICKED_GENERATE, { includes_directions: Boolean(teacherDirections) })

    try {
      const savedUuid = await run
      if (isTornDown) return
      analytics.sendEvent(GENERATED_ASSESSMENT_EVENTS.GENERATION_COMPLETED, { outcome: 'success' })
      if (targetKey(target.value) === runKey) resultUuid.value = savedUuid
      await refresh()
    } catch (error) {
      await recordFailure(runKey, error)
    } finally {
      if (activeRun === run) {
        isRunning.value = false
        activeRun = null
        setTurnDetail('', NO_TURN)
      }
    }
  }

  // Stops reading on leaving the page; the server still finishes and saves the run.
  onScopeDispose(() => {
    if (!activeRun) return
    isTornDown = true
    activeRun.close()
  })

  return {
    isModalOpen,
    target,
    isRunning,
    stage,
    detail,
    tick,
    resultUuid,
    errorMessage,
    errorKind,
    openModal,
    startGeneration
  }
}
